import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Paper, Typography} from "@mui/material";
import TryAgain from "../../../../../components/app-specific/try-again";
import useWindowSize from "../../../../../hooks/use-window/size";
import Api from "../../../../../../core/services/api/api";
import Iframe from "react-iframe";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ElementIds from "../../../../../../core/models/element-ids";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const DashboardView = () => {
    const [loading, setLoading] = useState(true);
    const [dashboards, setDashboards] = useState([]);
    const [publicDashboards, setPublicDashboards] = useState([]);
    const [selectedDashboardId, setSelectedDashboardId] = useState();
    const [size, setSize] = useState({height: 0, width: 0});
    const topSection = useRef();
    const windowSize = useWindowSize();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('dashboard-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const selectedDashboardInfo = useMemo(() => dashboards?.find(e => e.id === selectedDashboardId), [dashboards, selectedDashboardId]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.dashboard);
        configureBehavioralTool((tags) => tags.dashboard);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the list of available dashboards for the operator to choose from.
     * @type {(function(): Promise<void>)}
     */
    const getOperatorsDashboards = useCallback(async () => {
        setLoading(true);
        let dashboards = [];
        const privateDashboards = await Api.getAvailableDashboards()
        const publicDashboards = await Api.getPublicDashboards();
        if (!isMounted())
            return;
        if (privateDashboards?.resultFlag) {
            dashboards.push(...privateDashboards?.data ?? []);
        }
        if (publicDashboards?.resultFlag) {
            setPublicDashboards(publicDashboards?.data ?? []);
            dashboards.push(...publicDashboards?.data?.map(e => ({
                ...e,
                title: e.title?.concat(' (Public)')
            })) ?? []);
        }
        dashboards = [...(new Set(dashboards?.map(e => e.id) ?? []).values())].map(e => dashboards.find(r => r.id === e))
        setDashboards(dashboards);
        const defaultDashboard = dashboards.find(e => !!e.isDefault);
        setSelectedDashboardId((defaultDashboard ?? dashboards[0])?.id);
        setLoading(false);
    }, [isMounted]);

    /**
     * Loads the list available dashboards for the operator to choose from.
     */
    useEffect(() => {
        void getOperatorsDashboards()
    }, [getOperatorsDashboards]);

    /**
     * Listens for the changes of the window size and for each change, calculates the height and width required for
     * the iframe section.
     */
    useEffect(() => {
        if (!topSection?.current)
            return;
        const mainSection = document.getElementById(ElementIds.panel.main).getBoundingClientRect();
        const panelAppbar = document.getElementById(ElementIds.panel.appBar);
        mainSection.height -= panelAppbar.getBoundingClientRect().height;
        const dashboardTopSection = topSection.current.getBoundingClientRect();
        setSize(prevState => {
            const newHeight = mainSection.height - dashboardTopSection.height - 48;
            const newWidth = mainSection.width;
            if (newHeight === prevState.height && newWidth === prevState.width)
                return prevState;
            return {height: newHeight, width: newWidth};
        })
    }, [windowSize, topSection]);

    /**
     * Sets the selected dashboard as default for the operator.
     * @type {(function(*): Promise<void>)}
     */
    const setDashboardAsDefault = useCallback(async (dashboardId) => {
        const response = await Api.setDashboardAsDefault(dashboardId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setDashboards(prevState => prevState.map(e => ({...e, isDefault: e.id === dashboardId})))
        }
    }, [isMounted])

    /**
     * Selects the new dashboard and if non-public, sets it as default.
     * @type {(function(*=, *=): void)}
     */
    const onNewDashboardSelected = useCallback(async (_, newDashboardId) => {
        setSelectedDashboardId(newDashboardId);
        if (!publicDashboards?.find(e => e.id === newDashboardId)) {
            void setDashboardAsDefault(newDashboardId);
        }
    }, [publicDashboards, setDashboardAsDefault])

    return (
        <>
            <div ref={topSection} className={'w-100 pt-2 d-flex flex-column w-100'}>
                <Typography variant={'h6'} fontWeight={600} marginBlockEnd={2}>
                    Classic Dashboards
                </Typography>
                {
                    dashboards?.length > 0 &&
                    <Tabs className={'mb-3'} value={selectedDashboardId ?? false} onChange={onNewDashboardSelected}>
                        {
                            dashboards?.map(e => (
                                <Tab key={e.id} value={e.id} label={e.title}/>
                            ))
                        }
                    </Tabs>
                }
            </div>
            <Paper className='paper p-2' style={{height: size.height}}>
                {
                    loading ?
                        (
                            <div className={'loading chat'}>
                                <div/>
                            </div>
                        ) :
                        (
                            selectedDashboardId
                                ? <Iframe
                                    title={selectedDashboardInfo?.title}
                                    allowFullScreen
                                    className={'rounded-xl'}
                                    frameBorder={0}
                                    url={selectedDashboardInfo?.url}
                                    width='100%'
                                    height='100%'
                                    style={{display: 'block'}}
                                />
                                : <TryAgain
                                    text={'You have not yet selected any dashboards. Please select one, or contact your admin to assign dashboards to you.'}
                                    buttonText={'Reload My Dashboards'}
                                    onClick={getOperatorsDashboards}
                                />
                        )
                }
            </Paper>
        </>
    );
}

export default DashboardView;
